import { useMemo, type ReactNode } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Card, CardContent } from '@/components/ui/card'
import { usePlanMetadata } from '@/hooks/usePlanMetadata'
import { useCurrentVersionId } from '@/hooks/useCurrentVersionId'
import { cn } from '@/utils/cn'
import {
  buildPlanFinancialsRows,
  formatMetric,
  formatVariance,
  formatVariancePct,
} from './planFinancialsMockData'

interface PlanFinancialsTabProps {
  planId: string
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '-'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '-'
  return d.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: '2-digit',
  })
}

function statusLabel(status: string | undefined): string {
  if (!status) return '-'
  return status.replace(/_/g, ' ').toUpperCase()
}

function InfoField({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span className="truncate text-sm font-medium text-foreground">
        {value || '-'}
      </span>
    </div>
  )
}

export function PlanFinancialsTab({ planId }: PlanFinancialsTabProps) {
  const { data: plan } = usePlanMetadata(planId)
  const { versionId } = useCurrentVersionId(planId)

  const currentVersion = useMemo(
    () => plan?.versions.find(v => v.id === versionId) ?? plan?.versions[0],
    [plan, versionId]
  )

  const rows = useMemo(() => buildPlanFinancialsRows(planId), [planId])

  return (
    <div className="flex flex-col gap-4">
      {/* Title strip + info cards */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-stretch">
        <div className="flex flex-col justify-center lg:w-[200px] lg:shrink-0">
          <span className="text-xs italic text-destructive">
            Assortment Reports
          </span>
          <h2 className="text-2xl font-semibold text-foreground">Financials</h2>
        </div>

        <div className="flex flex-1 flex-col gap-3 lg:flex-row">
          <Card className="flex-1">
            <CardContent className="grid grid-cols-3 gap-4 p-4">
              <InfoField label="Status" value={statusLabel(plan?.status)} />
              <InfoField
                label="Version #"
                value={currentVersion?.version_number ?? '-'}
              />
              <InfoField
                label="Version Alias"
                value={currentVersion?.alias ?? '-'}
              />
              <InfoField label="Season Event" value={plan?.season?.name} />
              <InfoField
                label="Set/Reset date"
                value={formatDate(plan?.set_reset_date)}
              />
              <InfoField
                label="Exit date"
                value={formatDate(plan?.exit_date)}
              />
              <InfoField label="Season Start" value="-" />
              <InfoField label="Season End" value="-" />
              <div />
            </CardContent>
          </Card>

          <Card className="lg:w-[220px] lg:shrink-0">
            <CardContent className="flex h-full flex-col gap-2 p-4">
              <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                Notes
              </span>
              <p className="text-sm text-foreground whitespace-pre-wrap break-words">
                {plan?.notes?.trim() || '-'}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Financials table */}
      <Card className="overflow-hidden">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-[28%] border-l-4 border-l-[#84C4B4]" />
                <TableHead className="text-right text-xs font-semibold uppercase tracking-wide text-foreground">
                  Planned
                </TableHead>
                <TableHead className="text-right text-xs font-semibold uppercase tracking-wide text-foreground">
                  Assortment
                </TableHead>
                <TableHead className="text-right text-xs font-semibold uppercase tracking-wide text-foreground">
                  $ Var to Plan
                </TableHead>
                <TableHead className="text-right text-xs font-semibold uppercase tracking-wide text-foreground">
                  % Var to Plan
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(row => (
                <TableRow key={row.label}>
                  <TableCell className="border-l-4 border-l-[#84C4B4] bg-[#fafafa] font-medium text-foreground">
                    {row.label}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    {row.assortmentOnly
                      ? ''
                      : row.planned === null
                        ? '-'
                        : formatMetric(row.planned, row.format)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {row.assortment === null
                      ? '-'
                      : formatMetric(row.assortment, row.format)}
                  </TableCell>
                  <TableCell
                    className={cn(
                      'text-right tabular-nums',
                      row.variance !== null &&
                        row.variance < 0 &&
                        'text-destructive'
                    )}
                  >
                    {row.assortmentOnly || row.variance === null
                      ? ''
                      : formatVariance(row.variance, row.format)}
                  </TableCell>
                  <TableCell
                    className={cn(
                      'text-right tabular-nums',
                      row.variancePct !== null &&
                        row.variancePct < 0 &&
                        'text-destructive'
                    )}
                  >
                    {row.assortmentOnly ||
                    row.hideVariancePct ||
                    row.variancePct === null
                      ? ''
                      : formatVariancePct(row.variancePct)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  )
}
